import React, { useState, useEffect } from "react";
import "./App.css";
import { useNavigate, Link } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { BACKEND_URL } from "./config";
import bannerEvent from "./img/banner-event.webp";
import bannerEventMobile from "./img/banner-event-mobile.webp";

function App() {

  const getStoredValue = (key, defaultValue = "") => {
    return sessionStorage.getItem(key) || defaultValue;
  };

  const setStoredValue = (key, value) => {
    sessionStorage.setItem(key, value);
  };

  const [firstName, setFirstName] = useState(getStoredValue("first_name"));
  const [lastName, setLastName] = useState(getStoredValue("last_name"));
  const [dni, setDni] = useState(getStoredValue("dni"));

  const [email, setEmail] = useState(getStoredValue("email"));           
  const [emailError, setEmailError] = useState("");                                
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);      

  const [phone, setPhone] = useState(getStoredValue("phone"));
  const [position, setPosition] = useState(getStoredValue("position"));
  const [region, setRegion] = useState(getStoredValue("region"));
  const [sector, setSector] = useState(getStoredValue("sector"));
  const [employees, setEmployees] = useState(getStoredValue("employees"));
  const [invitationCode, setInvitationCode] = useState(getStoredValue("invitation_code"));
  
  const [formErrors, setFormErrors] = useState({});
  const [invitationError, setInvitationError] = useState("");
  const [invitationSuccess, setInvitationSuccess] = useState("");
  const [dniStatusError, setDniStatusError] = useState("");
  const [isValidatingDni, setIsValidatingDni] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  const navigate = useNavigate();

  const validarEmail = (valor) => {
    const regex = /^[A-Za-z0-9._%+-]{2,}@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,}$/;
    return regex.test(valor);
  };

  const CODE_RE = /^(?:EDD100|EED100)[A-Z]+$/;
  const BASE_PRICE = 185000; 

  const [codeValid, setCodeValid] = useState(false);
  const [finalPrice, setFinalPrice] = useState(BASE_PRICE);

  //validar email Y dni (backend) GET /validate-email/{email}?dni={dni}
  useEffect(() => {
    if (!email || !validarEmail(email) || !dni) return;

    const h = setTimeout(async () => {
      try {
        setIsValidatingEmail(true);
        setEmailError("");
        const res = await fetch(`${BACKEND_URL}/validate-email/${encodeURIComponent(email)}?dni=${dni}`);
        const data = await res.json();
        if (!data.valid) {
          const reason = data.reason;
          if (reason === "invited_exists") setEmailError("Este email ya está confirmado por código.");
          else if (reason === "approved") setEmailError("Este email ya tiene un pago aprobado.");
          else if (reason === "pending") setEmailError("Este email tiene un pago pendiente.");
          else if (reason === "in_process") setEmailError("Este email tiene un pago en proceso.");
          else if (reason === "email_in_use_by_other_dni") setEmailError("Este email ya está asociado a otro DNI.");
          else setEmailError("No se puede usar este email.");
        }
      } catch (e) {
        setEmailError("Error validando el email. Intentalo nuevamente.");
      } finally {
        setIsValidatingEmail(false);
      }
    }, 400);

    return () => clearTimeout(h);
  }, [email, dni]);

  // empresas (select + creación)
  const [company, setCompany] = useState(() => {
    const stored = sessionStorage.getItem("id_company");
    return stored ? parseInt(stored) : "";
  });

  const [companyOptions, setCompanyOptions] = useState([]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/companies`);
        const data = await res.json();
        const formatted = data.map((company) => ({
          label: company.company_name,
          value: company.id_company,
        }));
        setCompanyOptions(formatted);
      } catch (err) {
        console.error("Error al cargar empresas:", err);
      }
    };
    fetchCompanies();
  }, []);

  const handleCompanyChange = async (selectedOption) => {
    if (!selectedOption) {
      setCompany("");
      sessionStorage.removeItem("id_company");
      sessionStorage.removeItem("company_name");
      return;
    }

    const existing = companyOptions.find(
      (c) => c.label.toLowerCase() === selectedOption.label.toLowerCase()
    );

    if (existing) {
      setCompany(existing.value);
      setStoredValue("id_company", existing.value);
      setStoredValue("company_name", existing.label);
    } else {
      try {
        const res = await fetch(`${BACKEND_URL}/companies`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ company_name: selectedOption.label })
        });

        const data = await res.json();

        if (data && data.id_company) {
          const newCompany = {
            label: data.company_name,
            value: data.id_company
          };

          setCompanyOptions(prev => [...prev, newCompany]);
          setCompany(newCompany.value);
          setStoredValue("id_company", newCompany.value);
          setStoredValue("company_name", newCompany.label);
        } else {
          console.error("La respuesta del backend no tiene id_company:", data);
        }
      } catch (error) {
        console.error("Error al crear empresa:", error);
      }
    }
  };

  const styleSelect = {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'white',
      borderColor: '#C9C5CA',
      borderRadius: '5px',
      height: '27px',
      marginTop: '7px',
      marginBottom: '8px',
      marginLeft: '0px',
      marginRight: '10px',
      '&:hover': {
        borderColor: '#5E5D62',
      },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#eef2ff' : 'white',
      color: state.isSelected ? '#4f46e5' : '#111827',
      padding: 10,
      cursor: 'pointer',
    }),
    indicatorContainer: (base) => ({
      ...base,
      height: '15px',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#5E5D62',
      height: '27px',
      top: '8px',
      position: 'absolute',
      fontSize: '12px',
    }),
    input: (base) => ({
      ...base,
      color: '#5E5D62',
      height: '27px',
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10,
    }),
  };

  // validación de código de invitación (back) GET /validate-code/{code}
const handleInvitationCodeChange = (e) => {
  const upperCode = e.target.value.toUpperCase().trim();
  setInvitationCode(upperCode);
  setStoredValue("invitation_code", upperCode);
};

useEffect(() => {
  let cancelled = false;

  const run = async () => {
    const code = (invitationCode || "").trim();

    setInvitationError("");
    setInvitationSuccess("");

    if (!code) {
      if (!cancelled) {
        setCodeValid(false);
        setFinalPrice(BASE_PRICE);
      }
      return;
    }

    if (!CODE_RE.test(code)) {
      if (!cancelled) {
        setCodeValid(false);
        setFinalPrice(BASE_PRICE);
        setInvitationError("Formato inválido.");
      }
      return;
    }

    setIsValidatingCode(true);
    try {
      const res = await fetch(`${BACKEND_URL}/validate-code/${encodeURIComponent(code)}`);
      const data = await res.json();

      if (cancelled) return;

      if (data?.valid) {
        setCodeValid(true);
        setFinalPrice(0);
        setInvitationError("");
        setInvitationSuccess("Código aplicado: entrada sin cargo.");
      } else {
        setCodeValid(false);
        setFinalPrice(BASE_PRICE);
        setInvitationSuccess("");

        const reason = data?.reason || "unknown";
        if (reason === "limit_reached") {
          setInvitationError("Este código alcanzó su límite de uso.");
        } else if (reason === "not_found") {
          setInvitationError("Código no reconocido.");
        } else if (reason === "invalid_format") {
          setInvitationError("Formato inválido.");
        } else {
          setInvitationError("No se pudo corroborar el código.");
        }
      }
    } catch (_) {
      if (!cancelled) {
        setCodeValid(false);
        setFinalPrice(BASE_PRICE);
        setInvitationSuccess("");
        setInvitationError("Error corroborando el código.");
      }
    } finally {
      if (!cancelled) setIsValidatingCode(false);
    }
  };

  run();
  return () => { cancelled = true; };
}, [invitationCode, BACKEND_URL]);

  //validación de dni (back) GET /validate-dni/{dni}
  useEffect(() => {
    const validateDni = async () => {
      if (!dni) return;

      setIsValidatingDni(true);
      setDniStatusError("");

      try {

        const res = await fetch(`${BACKEND_URL}/validate-dni/${dni}`);
        if (!res.ok) throw new Error("Error del servidor");

        const data = await res.json();
        if (!data.valid) {
          const reason = data.reason;
          if (reason === "approved") {
            setDniStatusError("Este DNI ya se registró. Revisá tu correo electrónico para ver el resumen.");
          } else if (reason === "pending") {
            setDniStatusError("Este DNI tiene un pago pendiente. Esperá a que se confirme para volver a intentar.");
          } else if (reason === "rejected") {
            setDniStatusError("");
          } else if (reason === "invited") {
            setDniStatusError("Este DNI ya tiene un código de invitación cargado. No puede volver a registrarse.");
          } else if (reason === "in_process") {
            setDniStatusError("Este DNI tiene un pago en proceso. Esperá a que se confirme para volver a intentar.");
          } else if (reason === "missing_payment") {
            setDniStatusError("Este DNI no tiene datos de pago. Por favor, completá el proceso correctamente.");
          } else if (reason === "invalid_format") {
            setDniStatusError("El DNI ingresado no tiene el formato correcto.");
          }
        } else {
          setDniStatusError("");
        }
      } catch (err) {
        console.error("Error validando DNI:", err);
        setDniStatusError("Hubo un error validando el DNI. Intentalo nuevamente.");
      } finally {
        setIsValidatingDni(false); 
      }
    };

    validateDni();
  }, [dni]);


  const btnNext = () => {
    const errores = {};
    if (!firstName.trim()) errores.firstName = 'El nombre es obligatorio';
    if (!lastName.trim()) errores.lastName = 'El apellido es obligatorio';
    if (!dni.trim()) errores.dni = 'El DNI es obligatorio';
    if (!phone.trim()) errores.phone = 'El teléfono es obligatorio';
    if (!position.trim()) errores.position = 'El puesto es obligatorio';
    if (!company) errores.company = 'La empresa es obligatoria';
    if (!region.trim()) errores.region = 'La provincia es obligatoria';
    if (!sector.trim()) errores.sector = 'El rubro es obligatoria';
    if (!employees.trim()) errores.employees = 'Debe seleccionar una cantidad de empleados';
    if (!validarEmail(email)) { errores.email = "El email es inválido."; }

    
    if (invitationCode && !codeValid) {
    setInvitationError("❌ El código aún no es válido.");
    return;
  }

  if (dniStatusError || emailError) {
    setFormErrors(prev => ({
      ...prev,
      ...(dniStatusError ? { dni: dniStatusError } : {}),
      ...(emailError ? { email: emailError } : {})
    }));
    return;
  }

  setFormErrors(errores);

  if (Object.keys(errores).length === 0) {
    navigate("/emission");
  }
};
  const provinciasArg = ["Buenos Aires", "Catamarca", "Chaco", "Chubut", "Ciudad Autónoma de Buenos Aires", "Córdoba", "Corrientes", "Entre Ríos", "Formosa", "Jujuy", "La Pampa", "La Rioja", "Mendoza", "Misiones", "Neuquén", "Río Negro", "Salta", "San Juan", "San Luis", "Santa Cruz", "Santa Fe", "Santiago del Estero", "Tierra del Fuego, Antártida e Islas del Atlántico Sur", "Tucumán"];
  const sectorCompany = ["Operadoras", "Servicios Petroleros (perforación, completación, well services, etc.)", "Midstream", "Downstream", "Equipos y Manufactura", "Ingeniería y Construcción", "Transición Energética y Sustentabilidad", "Finanzas e Inversores", "Consultoría, Legal y Asesoría", "Sector público"];

  return (
    <div className="container">
      <img src={bannerEvent} alt="Econojournal Supplier Day 2025" className="bannerEvent" />
      <img src={bannerEventMobile} alt="Econojournal Supplier Day 2025" className="bannerEventMobile" />
      <h2>Formulario de inscripción</h2>
      <h3>Completá el formulario para inscribirte.</h3>
      <form>
        <div className="containerFlex">
          <div className="col-2">
            <label>
              Nombre <b>*</b>
            </label>
            <input
              type="text"
              name="first_name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setStoredValue("first_name", e.target.value);
              }}
              required
              minLength="3"
            />
            {formErrors.firstName && <span className="required">{formErrors.firstName}</span>}
          </div>

          <div className="col-2">
            <label>
              Apellido <b>*</b>
            </label>
            <input
              type="text"
              name="last_name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setStoredValue("last_name", e.target.value);
              }}
              required
              minLength="3"
            />
            {formErrors.lastName && <span className="required">{formErrors.lastName}</span>}
          </div>
        </div>

        <div className="containerFlex">
          <div className="col-2">
            <label>
              DNI <b>*</b>
            </label>
            <input
              className="match"
              type="text"
              inputMode="numeric"
              name="dni"
              value={dni}
              onChange={(e) => {
                setDni(e.target.value);
                setStoredValue("dni", e.target.value);
                setDniStatusError(""); 
                sessionStorage.removeItem("edit_mode");
              }}
              pattern="^\d{6,10}$"
              required
              placeholder="Ingresá tu DNI/LC/LE, sin puntos ni espacios"
              title="Ingresá solo números, entre 6 y 10 dígitos"
            />
            {formErrors.dni && <span className="required">{formErrors.dni}</span>}
            {dniStatusError && <span className="required">{dniStatusError}</span>}
          </div>

          <div className="col-2">
            <label>
              Email <b>*</b>
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setStoredValue("email", e.target.value);
              }}
              required
              title="Debes completar el campo con tu email"
            />
            {formErrors.email && <span className="required">{formErrors.email}</span>}
            {emailError && <span className="required">{emailError}</span>}
          </div>
        </div>

        <div className="containerFlex">
          <div className="col-2">
            <label>
              Teléfono <b>*</b>
            </label>
            <input
              className="match"
              type="tel"
              name="phone"
              pattern="^\+\d{11,18}$"
              maxLength={18}
              minLength={11}
              value={phone}
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/[^+\d]/g, "");
                setPhone(onlyNums);
                setStoredValue("phone", onlyNums);
              }}
              required
              title="Debes completar el campo con tu teléfono"
              placeholder="EJ: +54114234567"
            />
            {formErrors.phone && <span className="required">{formErrors.phone}</span>}
          </div>

          <div className="col-2">
            <label>
              Puesto <b>*</b>
            </label>
            <input
              type="text"
              name="position"
              value={position}
              onChange={(e) => {
                setPosition(e.target.value);
                setStoredValue("position", e.target.value);
              }}
              required
              title="Debes completar el campo con tu puesto"
            />
            {formErrors.position && <span className="required">{formErrors.position}</span>}
          </div>
        </div>

        <div className="containerFlex">
          <div className="col-2">
            <label>
              Empresa <b>*</b>
            </label>
            <CreatableSelect
              styles={styleSelect}
              isClearable
              value={companyOptions.find((c) => c.value === company) || null}
              onChange={handleCompanyChange}
              options={companyOptions}
              placeholder="Seleccioná o agregá tu empresa"
              formatCreateLabel={(inputValue) => `Agregar empresa "${inputValue}"`}
            />
            {formErrors.company && <span className="required">{formErrors.company}</span>}
          </div>

          <div className="col-2">
            <label>
              Provincia <b>*</b>
            </label>
            <select
              onChange={(e) => {
                setRegion(e.target.value);
                setStoredValue("region", e.target.value);
              }}
              value={region}
              required
              title="Debes completar el campo con una provincia"
            >
              <option value="" disabled>Seleccioná una provincia</option>
              {provinciasArg.map((mode) => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
            {formErrors.region && <span className="required">{formErrors.region}</span>}
          </div>
        </div>

        <div className="containerFlex">
          <div className="col-2">
            <label>
              Rubro <b>*</b>
            </label>
            <select
              onChange={(e) => {
                setSector(e.target.value);
                setStoredValue("sector", e.target.value);
              }}
              value={sector}
              required
              title="Debes completar el campo con el sector de tu empresa"
            >
              <option value="" disabled>Seleccioná un rubro</option>
              {sectorCompany.map((mode) => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
            {formErrors.sector && <span className="required">{formErrors.sector}</span>}
          </div>

          <div className="col-2">
            <label>
              Cantidad de empleados <b>*</b>
            </label>
            <select
              onChange={(e) => {
                setEmployees(e.target.value);
                setStoredValue("employees", e.target.value);
              }}
              value={employees}
              required
              title="Debes completar el campo con una cantidad de empleados"
            >
              <option value="" disabled>Seleccioná una cantidad</option>
              <option value="1 a 10">1 a 10</option>
              <option value="10 a 50">10 a 50</option>
              <option value="50 a 100">50 a 100</option>
              <option value="100 a 250">100 a 250</option>
              <option value="250 a 500">250 a 500</option>
              <option value="500 a 1.000">500 a 1.000</option>
              <option value="+ 1.000">Más de 1.000</option>
            </select>
            {formErrors.employees && <span className="required">{formErrors.employees}</span>}
          </div>
        </div>

        <div className="containerFlex">
          <label>Código de invitación</label>
          <input
            className="match"
            type="text"
            name="invitation_code"
            pattern="^(?:EDD100|EED100)[A-Z]+$"
            value={invitationCode}
            onChange={handleInvitationCodeChange}
            title="Ingresá tu código de invitación"
            placeholder="Pegá tu código de invitación. Si no tenés uno, dejá el campo vacío"
          />
          {invitationError && <span className="required">{invitationError}</span>}
          {invitationSuccess && <span className="success">{invitationSuccess}</span>}
        </div>

        <div>
          <p className="legal">*Los campos son requeridos</p>
          <p className="legal">
            Al enviar este formulario, aceptás nuestra {" "}
            <Link to="/privacy" target="blank">
              Política de Privacidad.
            </Link>
          </p>
        </div>

        <div className="containerFlex">
          <div className="card flexBetween">
            <div>
              <h2>Costo de la inscripción:</h2>
              <div className='totalEmission'>
                {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(finalPrice)}
              </div>

              <p>Econojournal emite <b>Factura B</b> por defecto. Si requerís <b>Factura A</b>, te pedimos que la solicites vía email</p>
              <p>apenas concretes la compra: <Link onClick={() => window.location = 'mailto:administracion@econojournal.com.ar'}>administracion@econojournal.com.ar</Link> <b>Por favor realizá el pedido antes del 15/12/2025 indicando CUIT y razón social.</b></p>
            </div>

            <div>
              <button
                type="button"
                onClick={btnNext}
                disabled={
                  isValidatingDni ||
                  isValidatingCode ||
                  isValidatingEmail || 
                  !!dniStatusError ||
                  !!emailError ||     
                  Object.keys(formErrors).length > 0
                }
              >
                {(isValidatingDni || isValidatingCode || isValidatingEmail)
                  ? "Validando..."
                  : "Siguiente"}
              </button>
            </div>
          </div>
        </div>

        <h4 className="footerCopyright">Design, code, words, and hard work by <Link to="https://wcm.media" target="blank">Wildcat Media.</Link></h4>
      </form>
    </div>
  );
}

export default App;
